import { COURSE_TYPE, RequestMeta } from "@types";
import { FC, useEffect, useState } from "react";
import { EXPLORE_SORT } from "enums/new-explore/explore-sort";
import newExploreApi from "@api/new-explore";
import {
  exploreMethodBySlug,
  NewExploreCoursesResponse,
  NewExplorePayload,
  NewExploreProduct,
} from "types/api/new-explore";
import { useRouter } from "next/router";
import Card from "@shared/components/card";
import BookIcon from "@svg/new-design/book";
import { scrollToElementById } from "@utils/ui";
import { InstructorProductCardsSkeleton } from "./skeleton/InstructorsProductsSkeleton";
import { useLayoutContext } from "@context/layout";
import useQueryParams from "@shared/hooks/useQueryParams";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import ProductTab from "./ProductTab";
import InstructorProductCard from "./instructorProductCard";
import { CARD_VARIANT } from "@shared/components/card/types";
import {
  Pagination,
  PAGINATION_BUTTON_VARIANT,
} from "@shared/components/pagination";

interface Props {
  products: NewExploreProduct[] | undefined;
  id: string;
}
export const InstructorPaginationLimit = 8;

const InstructorProduct: FC<Props> = ({ id, products }) => {
  const { query } = useRouter();
  const [pagination, setPagination] = useState({
    page: Number(query.page) || 1,
    totalPage: 1,
    total: products?.length,
  });
  const { setParam } = useQueryParams();
  const [productsData, setProducts] = useState<NewExploreProduct[] | undefined>(
    products
  );
  const { isAdmin } = useLayoutContext();
  const offset = isAdmin ? 146 : 96;
  const [loading, setLoading] = useState(products === undefined);

  const payload = {
    page: Number(query.page) || 1,
    sort: (query.sort as EXPLORE_SORT) || EXPLORE_SORT.MOST_SOLD,
    count: InstructorPaginationLimit,
    instructor_id: String(id),
    bypass_exclusion: 1,
  };
  const { data } = useRequestImmutable(
    (
      newExploreApi[exploreMethodBySlug.course] as (
        // eslint-disable-next-line no-unused-vars
        params?: NewExplorePayload
      ) => RequestMeta<NewExploreCoursesResponse, never, NewExplorePayload>
    )(payload)
  );

  useEffect(() => {
    if (data) {
      setProducts(data.products);
      setPagination({
        page: data.pagination.page,
        totalPage: data.pagination.total_pages,
        total: data.pagination.total_items,
      });
      setLoading(false);
      if (query.page || query.sort)
        scrollToElementById(
          isMobile ? "products" : "instructorProducts",
          offset
        );
    }
  }, [data]);

  const handleSortClick = (sort: EXPLORE_SORT) => {
    setParam({
      key: "sort",
      value: sort,
      redirect: true,
      shallow: false,
      replace: true,
    });
    setLoading(true);
  };
  const handleClickOnPage = (page: number) => {
    setLoading(true);
    setParam({
      key: "page",
      value: String(page),
      redirect: true,
      shallow: false,
      replace: true,
    });
  };
  const isMobile = useIsMobile(1240);
  return (
    <div className="fds-l:px-6  pt-6  mb-6  fds-l:mb-0" id="instructorProducts">
      <div className="flex items-center">
        <BookIcon className="fds-l:hidden block ml-1 fds-l:ml-0 w-6  h-6  text-fds-icon-tertiary" />
        <p className="fds-l:headline-lg headline-sm ">آموزش‌های مدرس</p>
      </div>

      {Boolean(
        pagination.total && pagination.total >= InstructorPaginationLimit
      ) && (
        <ProductTab
          count={pagination.total}
          handleClickOnSort={handleSortClick}
        />
      )}
      <div className="flex flex-col mt-4     fds-l:mt-6  gap-y-6">
        {!loading ? (
          (productsData || [])?.map((product) => (
            <div key={product.id}>
              <div className="hidden fds-l:block">
                <InstructorProductCard
                  svg_featured_image={product.svg_featured_image}
                  png_featured_image={product.png_featured_image}
                  key={product.id}
                  png_image={product.png_image}
                  title={product.title_short || product.title}
                  total_orders={(product as NewExploreProduct)?.total_student}
                  is_buy={product.is_buy}
                  status={product.status}
                  price={product.price}
                  watch_percent={product.watchTime_Percent}
                  id={product.id}
                  festival_new={product.festival_new}
                  hasCertificate={product.hasCertificate || false}
                  sku={product.sku || ""}
                  in_cart={product.in_cart || false}
                  duration={product.duration}
                  svg_image={product.svg_image}
                  slug={product.slug}
                  type={product.type}
                  isFavorite={product.is_favorite}
                />
              </div>
              <div className="block fds-l:hidden">
                <Card
                  {...product}
                  hugElement={false}
                  variant={CARD_VARIANT.LIST}
                  id={product.id}
                  isBuy={product.is_buy}
                  isFavorite={product.is_favorite}
                  sku={product.sku}
                  slug={product.slug || ""}
                  status={product.status}
                  title={product.title_short || product.title}
                  type={
                    (product as NewExploreProduct).type || COURSE_TYPE.COURSE
                  }
                  watchPercent={product.watchTime_Percent || 0}
                  duration={product.duration}
                  festival={product.festival_new}
                  instructor={product.instructors}
                  instructorCount={product.instructors?.length}
                  price={product.price}
                  showDetail={true}
                  showTag={false}
                  showTeacher={false}
                  totalOrders={product.total_student}
                  hasCertificate={product.hasCertificate || false}
                  noBorder={false}
                  inCart={product.in_cart || false}
                />
              </div>
            </div>
          ))
        ) : (
          <InstructorProductCardsSkeleton />
        )}
      </div>
      {pagination.total && pagination.total > InstructorPaginationLimit && (
        <div className="mt-12 flex w-full items-center justify-center">
          <Pagination
            currentPage={pagination.page}
            variant={
              isMobile
                ? PAGINATION_BUTTON_VARIANT.ARROW
                : PAGINATION_BUTTON_VARIANT.TEXT
            }
            totalPages={pagination.totalPage}
            pageItemsToShow={3}
            onPageChange={handleClickOnPage}
          />
        </div>
      )}
    </div>
  );
};

export default InstructorProduct;
